import isNumber from 'lodash/isNumber'
import upperFirst from 'lodash/upperFirst'
import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'temporal-polyfill/global'

import { AgencySettings_LineTypesEnum, AgencySettings_LocationTypesEnum, Provider } from 'src/api/gen/api'
import {
  getProviderSection,
  getVehicleTypeSection,
  getPropulsionTypeSection,
  getLocationTypeSection,
  getCloseMeansOfTransportSection,
} from 'src/commons/filters/sections'
import { ISection } from 'src/commons/filters/types'
import { getEnumEntries, getLocationTypeEntries, getProviderEntries } from 'src/commons/filters/utils'
import useSafeEffect from 'src/commons/hooks/useSafeEffect'
import { enumsTranslationKeys } from 'src/commons/i18nEnums'
import { ORDERED_GRANULARITIES } from 'src/commons/orderedList'
import { userHasPermission } from 'src/commons/permissions'
import { IFilters } from 'src/commons/types'
import { useDayOfWeekEntry } from 'src/components/DayOfWeek'
import { Slider } from 'src/components/Slider'
import { useAgencySettings, useAgencySettingsDistanceUnit, useAgencySettingsSpeedUnit } from 'src/layout/AgencySettings'
import { useMapLayer } from 'src/layout/MapLayerWrapper'
import CollapsibleFilter from 'src/muiv5/components/commons/SidePanel/Filters/BaseFilter/CollapsibleFilter'
import CheckboxListFilter from 'src/muiv5/components/commons/SidePanel/Filters/CheckboxListFilter/CheckboxListFilter'
import SliderFilter from 'src/muiv5/components/commons/SidePanel/Filters/Slider/SliderFilter'
import TimeRangeFilterPro from 'src/muiv5/components/commons/SidePanel/Filters/TimeRangeFilter/TimeRangeFilterPro'
import { SidePanel } from 'src/muiv5/components/commons/SidePanel/SidePanel'
import SETTINGS from 'src/settings'

import { formatDuration } from '../Trips/common'
import {
  DEFAULT_MAX_DISTANCE,
  DEFAULT_MAX_DURATION,
  DEFAULT_MAX_SPEED,
  DEFAULT_MIN_DISTANCE,
  DEFAULT_MIN_DURATION,
  DEFAULT_MIN_SPEED,
} from '../Trips/constants'

import { DashboardRouteMapping } from './Tabs'

export const useLocationTypesSection = function (): ISection {
  const agencySettings = useAgencySettings()

  return getLocationTypeSection({
    entries: getLocationTypeEntries(
      agencySettings.location_types.filter(
        (location_type) => location_type !== AgencySettings_LocationTypesEnum.ends_on_side
      ),
      enumsTranslationKeys.LocationType,
      true
    ),
    filterType: 'locationTypes',
  })
}

interface IProps {
  dashboardRouteMapping?: DashboardRouteMapping
  defaultFilters: IFilters
  initialFilters: IFilters
  isSensorsTripsSelected?: boolean
  joindedTimeRange?: boolean
  providers: Provider[]
  shouldNotDislayCloseMeansOfTransport?: boolean
  shouldNotDisplayLayer?: boolean
  shouldNotDisplayPropulsionType?: boolean
}

const FilterPanel = forwardRef(function (props: IProps, ref) {
  const agencySettings = useAgencySettings()
  const mapLayers = useMapLayer()
  const { value: distanceUnit, symbol: distanceUnitSymbol } = useAgencySettingsDistanceUnit()
  const { value: speedUnit, symbol: speedUnitSymbol } = useAgencySettingsSpeedUnit()

  const [filters, setFilters] = useState(props.initialFilters)
  useImperativeHandle(ref, () => filters, [filters])

  function onFilterChange(key: string, newValue: any) {
    return setFilters({ ...filters, [key]: newValue })
  }

  const durationMin = DEFAULT_MIN_DURATION
  const durationMax = DEFAULT_MAX_DURATION
  const distanceMin = DEFAULT_MIN_DISTANCE[distanceUnit]
  const distanceMax = DEFAULT_MAX_DISTANCE[distanceUnit]
  const speedMin = DEFAULT_MIN_SPEED[speedUnit]
  const speedMax = DEFAULT_MAX_SPEED[speedUnit]

  // There can only be one permit layer per jurisdiction
  let jurisdictionPermitLayerId: string | undefined
  const permitLayers = mapLayers.filter((layer) => layer.permitLayer)
  if (!props.shouldNotDisplayLayer && permitLayers.length > 0) {
    jurisdictionPermitLayerId = permitLayers[0].id
  }

  const { t } = useTranslation()
  const dayOfWeekEntry = useDayOfWeekEntry()

  useSafeEffect(() => {
    if (jurisdictionPermitLayerId) {
      onFilterChange('layer', jurisdictionPermitLayerId)
    }
  }, [jurisdictionPermitLayerId])

  const locationTypeSection = useLocationTypesSection()

  function onDurationChange(newDuration: number | [number, number]) {
    if (isNumber(newDuration)) {
      return
    }
    onFilterChange('duration', newDuration)
  }

  function onDistanceChange(newDistance: number | [number, number]) {
    if (isNumber(newDistance)) {
      return
    }
    onFilterChange('distance', newDistance)
  }

  function onSpeedChange(newSpeed: number | [number, number]) {
    if (isNumber(newSpeed)) {
      return
    }
    onFilterChange('speed', newSpeed)
  }

  const checkboxesSections: ISection<keyof IFilters>[] = []
  // We don't want summarize:kpi users to see the Provider's filter pannel at all
  if (userHasPermission(['read', 'kpi'])) {
    // add granularity and map layer sections to FilterCheckboxesSections
    // when multiplicity is handled properly (cf above, with onCheckboxFilterChange)
    const providerEntries = getProviderEntries(props.providers, undefined, true)
    const providerSection = getProviderSection({
      entries: providerEntries,
      filterType: 'providers',
      formatValue: false,
    })
    // we want to mask the provider section in the case of a user with only one provider access
    if (providerEntries.length > 1) {
      checkboxesSections.push(providerSection)
    }
    checkboxesSections.push(
      getVehicleTypeSection({
        entries: getEnumEntries(agencySettings.vehicle_types, enumsTranslationKeys.DeviceType),
        filterType: 'vehicleTypes',
      })
    )
    if (!props.shouldNotDisplayPropulsionType) {
      checkboxesSections.push(
        getPropulsionTypeSection({
          entries: getEnumEntries(agencySettings.vehicle_propulsions, enumsTranslationKeys.DevicePropulsion),
          filterType: 'propulsions',
        })
      )
    }
    if (!props.shouldNotDislayCloseMeansOfTransport) {
      checkboxesSections.push(
        getCloseMeansOfTransportSection({
          entries: getEnumEntries(
            [
              AgencySettings_LineTypesEnum.subway,
              AgencySettings_LineTypesEnum.train,
              AgencySettings_LineTypesEnum.tram,
            ],
            enumsTranslationKeys.LineType
          ),
          filterType: 'closeMeansOfTransport',
        })
      )
    }
  }

  if (props.isSensorsTripsSelected && userHasPermission(['read', 'trip'])) {
    checkboxesSections.push(locationTypeSection)
  }

  function onStartFromChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('range', { from: value, to: filters.range.to })
  }

  function onStartToChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('range', { from: filters.range.from, to: value })
  }

  function onStartBothChanged(valueFrom?: Temporal.ZonedDateTime, valueTo?: Temporal.ZonedDateTime) {
    onFilterChange('range', { from: valueFrom, to: valueTo })
  }

  function onEndFromChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('endRange', { from: value, to: filters.endRange?.to })
  }

  function onEndToChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('endRange', { from: filters.endRange?.from, to: value })
  }

  function onEndBothChanged(valueFrom?: Temporal.ZonedDateTime, valueTo?: Temporal.ZonedDateTime) {
    onFilterChange('endRange', { from: valueFrom, to: valueTo })
  }

  return (
    <SidePanel
      headerProps={{
        title: t('filtersPanel.title.default'),
        onReset: () => setFilters(props.defaultFilters),
      }}
    >
      <TimeRangeFilterPro
        filterId={'range'}
        format="L"
        values={{
          from: filters.range.from,
          to: filters.range.to,
        }}
        title={upperFirst(props.joindedTimeRange ? t('time range') : t('trips.startTime'))}
        onFromChanged={onStartFromChanged}
        onToChanged={onStartToChanged}
        onBothChanged={onStartBothChanged}
        disableFuture
        missingValue={!(filters.range.from && filters.range.to)}
      />
      {!props.joindedTimeRange && (
        <TimeRangeFilterPro
          filterId={'endRange'}
          format="L"
          values={{
            from: filters.endRange?.from,
            to: filters.endRange?.to,
          }}
          title={upperFirst(t('trips.endTime'))}
          onFromChanged={onEndFromChanged}
          onToChanged={onEndToChanged}
          onBothChanged={onEndBothChanged}
          disableFuture
          missingValue={!(filters.endRange?.from && filters.endRange?.to)}
        />
      )}
      <CheckboxListFilter
        filterId={'granularity'}
        items={ORDERED_GRANULARITIES.map((key) => ({
          id: key,
          label: upperFirst(t(enumsTranslationKeys.Time[key])),
        }))}
        values={[filters.granularity]}
        title={upperFirst(t('granularity'))}
        unique
        onFilterChange={(key: string, newValue: string[]) => onFilterChange(key, newValue[newValue.length - 1])}
        displayCheckbox
      />
      {filters.day_of_week && (
        <CheckboxListFilter
          filterId={'day_of_week'}
          items={dayOfWeekEntry}
          values={filters.day_of_week}
          title={upperFirst(t('trips.dayOfWeek'))}
          onFilterChange={onFilterChange}
          displayCheckbox
        />
      )}
      {filters.duration && (
        <CollapsibleFilter
          filterId={'duration'}
          title={upperFirst(t('trips.duration'))}
          subtitle={t('filters.date.rangeDurationSubtitle', {
            start: formatDuration(Math.max(filters.duration[0], durationMin)),
            end: formatDuration(Math.min(filters.duration[1], durationMax)),
          })}
          defaultExpanded
        >
          <SliderFilter
            value={[Math.max(filters.duration[0], durationMin), Math.min(filters.duration[1], durationMax)]}
            min={durationMin}
            max={durationMax}
            onChange={onDurationChange}
            unit={t('min')}
            formatValue={(durationInMinutes: number) => formatDuration(durationInMinutes)}
            disabled={!(durationMin || durationMax)}
            disableFuture={true}
            clearable={true}
          />
        </CollapsibleFilter>
      )}

      {agencySettings.use_telemetry_data && filters.distance ? (
        <CollapsibleFilter filterId={'distance'} title={upperFirst(t('trips.distance'))}>
          <Slider
            value={[Math.max(filters.distance[0], distanceMin), Math.min(filters.distance[1], distanceMax)]}
            min={distanceMin}
            max={distanceMax}
            step={SETTINGS.app.filter_trips_step}
            onChange={onDistanceChange}
            unit={t(distanceUnitSymbol)}
            formatValue={(distance: number) => `${distance.toString()} ${t(distanceUnitSymbol)}`}
            disableFuture={true}
            disabled={!(distanceMin || distanceMax)}
            clearable={true}
          />
        </CollapsibleFilter>
      ) : (
        ''
      )}

      {agencySettings.use_telemetry_data && filters.speed ? (
        <CollapsibleFilter filterId={'speed'} title={upperFirst(t('trips.speed'))}>
          <Slider
            value={[Math.max(filters.speed[0], speedMin), Math.min(filters.speed[1], speedMax)]}
            min={speedMin}
            max={speedMax}
            onChange={onSpeedChange}
            unit={t(speedUnitSymbol)}
            formatValue={(speed: number) => `${speed.toString()} ${t(speedUnitSymbol)}`}
            disableFuture={true}
            disabled={!(speedMin || speedMax)}
            clearable={true}
          />
        </CollapsibleFilter>
      ) : (
        ''
      )}
      {checkboxesSections.map((section, idx) => (
        <CheckboxListFilter
          key={`checkboxlistfilter-${idx}`}
          filterId={section.filterType}
          items={section.entries}
          values={filters[section.filterType] as string[]}
          title={section.title}
          onFilterChange={onFilterChange}
          displayCheckbox
        />
      ))}

      {!props.shouldNotDisplayLayer && (
        <CheckboxListFilter
          filterId={'layer'}
          items={mapLayers}
          values={filters.layer ? [filters.layer] : undefined}
          title={upperFirst(t('map layer'))}
          unique
          autocomplete
          onFilterChange={(key: string, newValue: string[]) => onFilterChange(key, newValue[newValue.length - 1])}
          displayCheckbox
        />
      )}
    </SidePanel>
  )
})

export default FilterPanel
